import { memo, useMemo } from 'react';

import { Modal, Button, Empty } from 'antd';

import { CandidateInterview, InterviewMessage } from '../../../../../fsm/jobs';
import { apiInstantToDate } from '../../../../../utils/apiInstant';

import styles from './index.module.css';

interface Props {
  name?: string;
  candidateInterview: CandidateInterview | null;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
}

const speakerLabel = (speaker: InterviewMessage['speaker'], name?: string) => {
  if (speaker === 'RIO') return 'Rio';
  if (speaker === 'CANDIDATE') return name || 'Candidate';
  return 'Unknown';
};

const dayFormat = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});
const timeFormat = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
});
const fullFormat = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

/**
 * The header's one line about the whole record: the day, the span and how long it ran, then whether it finished.
 * "Oct 16, 2026 · 10:53 AM – 11:26 AM · 33 min · Completed". Times the API could not give are left out, not guessed.
 */
const describeRecord = (
  candidateInterview: CandidateInterview | null,
): string | null => {
  const times = (candidateInterview?.transcript.conversations ?? [])
    .flatMap((conversation) => conversation.messages)
    .map((message) => apiInstantToDate(message.sentAt))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());
  const parts: string[] = [];
  if (times.length > 0) {
    const first = times[0];
    const last = times[times.length - 1];
    const sameDay = dayFormat.format(first) === dayFormat.format(last);
    parts.push(
      sameDay
        ? `${dayFormat.format(first)} · ${timeFormat.format(first)} – ${timeFormat.format(last)}`
        : `${fullFormat.format(first)} – ${fullFormat.format(last)}`,
    );
    const minutes = Math.round((last.getTime() - first.getTime()) / 60000);
    if (minutes > 0) parts.push(`${minutes} min`);
  }
  if (candidateInterview?.completed) parts.push('Completed');
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * Consecutive messages from the same side, as one group: one name and time above them, the bubbles tight together,
 * as a chat shows a reply sent in two parts. Order is kept; a new group starts whenever the speaker changes.
 */
const groupBySpeaker = (messages: InterviewMessage[]): InterviewMessage[][] =>
  messages.reduce<InterviewMessage[][]>((groups, message) => {
    const last = groups[groups.length - 1];
    if (last && last[0].speaker === message.speaker) last.push(message);
    else groups.push([message]);
    return groups;
  }, []);

/**
 * The current interview's transcript, read by the hiring team.
 *
 * Deliberately separate from {@code RioHistoryModal}: that component reads the
 * legacy `Dev.RioChatLog` transcript (a disconnected old system, keyed and
 * shaped differently). This one reads the live `InterviewTranscripts-{env}`
 * data via `GET /api/jobs/:jobId/candidates/:candidateId/interview`. It is a
 * chat, laid out like the candidate's own chat in avrio-rio, on this app's
 * design tokens (see index.module.css for why).
 *
 * Times come from the API as epoch seconds (apiInstantToDate). The day and
 * span are said once, in the header; each group of messages carries the time
 * of its first message, and every bubble has its full date and time on hover.
 *
 * Empty state wording is deliberately neutral ("No interview record found"),
 * not "Candidate hasn't interviewed yet": an empty response can also mean the
 * interview hasn't been invoked yet, the underlying table isn't provisioned in
 * this environment, or the identity join between systems didn't resolve. None
 * of those are the hiring team's problem to diagnose, so the copy doesn't
 * assert a specific cause it can't verify.
 */
const InterviewTranscriptModal = ({
  name,
  candidateInterview,
  isOpen,
  isLoading,
  onClose,
}: Props) => {
  const conversations = (
    candidateInterview?.transcript.conversations ?? []
  ).filter((conversation) => conversation.messages.length > 0);
  const hasMessages = conversations.length > 0;
  const meta = useMemo(
    () => describeRecord(candidateInterview),
    [candidateInterview],
  );

  return (
    <Modal
      title={
        <div className={styles.title}>
          <span>Interview transcript</span>
          {meta && <span className={styles.meta}>{meta}</span>}
        </div>
      }
      open={isOpen}
      width={760}
      confirmLoading={isLoading}
      onCancel={() => onClose()}
      footer={
        <Button type="primary" onClick={() => onClose()}>
          Close
        </Button>
      }
    >
      {hasMessages ? (
        <div className={styles.content}>
          {conversations.map((conversation, index) => {
            const started = apiInstantToDate(
              conversation.startedAt ?? conversation.messages[0]?.sentAt,
            );
            return (
              <section
                key={conversation.convoId ?? `conversation-${index}`}
                className={styles.session}
              >
                {/* Only when there is more than one sitting: a single interview needs no "Session 1". */}
                {conversations.length > 1 && (
                  <h4 className={styles.sessionHeading}>
                    Session {index + 1}
                    {started ? ` · ${fullFormat.format(started)}` : ''}
                  </h4>
                )}
                <ol className={styles.thread}>
                  {groupBySpeaker(conversation.messages).map((group) => {
                    const first = apiInstantToDate(group[0].sentAt);
                    const speaker = group[0].speaker;
                    return (
                      <li
                        key={group[0].msgId}
                        className={`${styles.group} ${speaker === 'CANDIDATE' ? styles.candidate : styles.rio}`}
                      >
                        <div className={styles.who}>
                          <span className={styles.speaker}>
                            {speakerLabel(speaker, name)}
                          </span>
                          {first && (
                            <time
                              className={styles.time}
                              dateTime={first.toISOString()}
                              title={fullFormat.format(first)}
                            >
                              {timeFormat.format(first)}
                            </time>
                          )}
                        </div>
                        {group.map((message) => {
                          const sent = apiInstantToDate(message.sentAt);
                          return (
                            <p
                              key={message.msgId}
                              className={styles.bubble}
                              title={sent ? fullFormat.format(sent) : undefined}
                            >
                              {message.body}
                            </p>
                          );
                        })}
                      </li>
                    );
                  })}
                </ol>
              </section>
            );
          })}
        </div>
      ) : (
        <Empty description="No interview record found" />
      )}
    </Modal>
  );
};

export default memo(InterviewTranscriptModal);
